import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { add, startOfToday } from 'date-fns';
import { merge, Subject } from 'rxjs';
import { filter, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import { sortByStartDate } from 'app/events/helpers/sort-by-start-date';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { debounceAllButFirst } from 'helpers';

import { CalendarEventDetailsFragment, SearchCalendarEventsGQL } from 'generated/graphql';

/**
 * Keyword + date range search over calendar events, with a selectable
 * result list. Owns its own query; emits the selected event(s).
 */
@Component({
  selector: 'app-event-search-select',
  templateUrl: './event-search-select.component.html',
})
export class EventSearchSelectComponent extends WithDisposableSubject() implements OnInit {
  @Input() campaignId: string;

  /**
   * Optional. Passed through verbatim so `entityResponse` is resolved for
   * that entity. Left undefined when a consumer doesn't need responses -
   * Apollo caches by field args, so don't default this.
   */
  @Input() entityId?: string;

  @Input() allowSelectAll = false;

  @Output() eventsSelected = new EventEmitter<CalendarEventDetailsFragment[]>();

  today = startOfToday();
  events: CalendarEventDetailsFragment[] = [];
  eventsLoading = true;
  selectedEventIds: string[] = [];

  eventSearchCtrl = new FormControl<string>(null);
  dateRangeFilter = new FormGroup({
    start: new FormControl<Date>(this.today),
    end: new FormControl<Date>(add(this.today, { months: 3 })),
  });

  private _dateChanged$ = new Subject<void>();

  constructor(
    private _searchCalendarEventsGQL: SearchCalendarEventsGQL,
    private _cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    merge(
      this.eventSearchCtrl.valueChanges.pipe(debounceAllButFirst(250)),
      this._dateChanged$
    ).pipe(
      // Load the initial list
      startWith(null),
      // Don't query on a partially entered range
      filter(() => {
        const { start, end } = this.dateRangeFilter.value;
        return !!start && !!end;
      }),
      tap(() => {
        this.eventsLoading = true;
        this._cd.markForCheck();
      }),
      switchMap(() => {
        const { start, end } = this.dateRangeFilter.value;
        return this._searchCalendarEventsGQL.fetch({
          campaignId: this.campaignId,
          entityId: this.entityId,
          keyword: this.eventSearchCtrl.value,
          dateRange: {
            min: start.toISOString(),
            max: end.toISOString(),
          },
        }, {
          // Always re-check the server - events may have been created since
          // this dialog was last opened.
          fetchPolicy: 'network-only'
        });
      }),
      takeUntil(this.disposer)
    ).subscribe(({ data }) => {
      this.events = [...data.searchCalendarEvents.nodes].sort(sortByStartDate);
      this.eventsLoading = false;
      this._cd.markForCheck();
    });
  }

  onDateChange() {
    this._dateChanged$.next();
  }

  onSelectionChanged(eventIds: string[]) {
    this.selectedEventIds = eventIds;
    this.eventsSelected.emit(
      eventIds
        .map(id => this.events.find(e => e.id === id))
        .filter(e => !!e)
    );
  }
}
